import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Card, Table, Button, Form, Row, Col, Badge, Spinner, Alert, InputGroup, Modal
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../layouts/Header';
import axiosInstance from '../services/axiosInterceptor';

const STATUS = {
  unpaid: { label: 'ค้างชำระ', bg: 'danger' },
  partial: { label: 'ชำระบางส่วน', bg: 'warning' },
  paid: { label: 'ชำระครบ', bg: 'success' },
  cancelled: { label: 'ยกเลิก', bg: 'secondary' }
};

const baht = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

const PAGE_LIMIT = 200;

const FeeInvoices = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(location.state?.termId || '');
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [capped, setCapped] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showGenModal, setShowGenModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/api/fee/terms');
        if (res.data.success) {
          setTerms(res.data.data);
          if (!selectedTerm && res.data.data.length > 0) setSelectedTerm(res.data.data[0].term_id);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'โหลดงวดไม่สำเร็จ');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Summary is a lightweight aggregate — safe to load whenever the term changes.
  const fetchSummary = async (termId) => {
    if (!termId) { setSummary(null); return; }
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${termId}/summary`);
      if (res.data.success) setSummary(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดยอดสรุปไม่สำเร็จ');
    }
  };

  // The invoice list is fetched on demand (search / status filter) and capped,
  // so we never pull the whole term's invoices into the browser at once.
  const fetchInvoices = async (termId, opts = {}) => {
    if (!termId) { setInvoices([]); return; }
    const status = opts.status !== undefined ? opts.status : statusFilter;
    setLoading(true);
    setError('');
    try {
      const res = await axiosInstance.get(`/api/fee/terms/${termId}/invoices`, {
        params: { status, search, limit: PAGE_LIMIT }
      });
      if (res.data.success) {
        setInvoices(res.data.data);
        setCapped(res.data.data.length >= PAGE_LIMIT);
        setSearched(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'ค้นหาบิลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  // Changing the term only reloads the summary and resets the (unsearched) list.
  useEffect(() => {
    setInvoices([]);
    setSearched(false);
    setCapped(false);
    fetchSummary(selectedTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerm]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    fetchInvoices(selectedTerm, { status: value });
  };

  const currentTermLabel = useMemo(() => {
    const t = terms.find((x) => String(x.term_id) === String(selectedTerm));
    return t ? `${t.year_year} / ภาค ${t.semester}` : '';
  }, [terms, selectedTerm]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setGenResult(null);
    try {
      const res = await axiosInstance.post(`/api/fee/terms/${selectedTerm}/generate`);
      if (res.data.success) {
        setGenResult(res.data.data);
        fetchSummary(selectedTerm);
        if (searched) fetchInvoices(selectedTerm);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'ออกบิลไม่สำเร็จ');
      setShowGenModal(false);
    } finally {
      setGenerating(false);
    }
  };

  const overall = summary?.overall;

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="mb-1">ออกบิล / รับชำระ</h2>
              <p className="text-muted mb-0">ออกใบแจ้งหนี้รายนักเรียนตามงวด และติดตามการชำระเงิน</p>
            </div>
            <Button variant="primary" disabled={!selectedTerm} onClick={() => { setGenResult(null); setShowGenModal(true); }}>
              <i className="ri-file-add-line me-1" /> ออกบิลทั้งงวด
            </Button>
          </div>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={5}>
                  <Form.Label>เลือกงวด/ภาคเรียน</Form.Label>
                  <Form.Select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
                    <option value="">-- เลือกงวด --</option>
                    {terms.map((t) => (
                      <option key={t.term_id} value={t.term_id}>
                        {t.year_year} / ภาค {t.semester} {t.term_name ? `(${t.term_name})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                {currentTermLabel && (
                  <Col md="auto">
                    <Badge bg="light" text="dark" className="fs-6">งวดที่เลือก: {currentTermLabel}</Badge>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          {overall && (
            <Row className="g-3 mb-3">
              <Col md={6} xl={3}>
                <Card className="h-100 border-primary"><Card.Body>
                  <small className="text-muted">ยอดเรียกเก็บรวม</small>
                  <h4 className="mb-0 mt-1">{baht(overall.total_amount)}</h4>
                  <small className="text-muted">{overall.invoice_count} ใบ</small>
                </Card.Body></Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-success"><Card.Body>
                  <small className="text-muted">รับชำระแล้ว</small>
                  <h4 className="mb-0 mt-1 text-success">{baht(overall.paid_amount)}</h4>
                  <small className="text-muted">ชำระครบ {overall.paid_count} ใบ</small>
                </Card.Body></Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-danger"><Card.Body>
                  <small className="text-muted">ยอดค้างชำระ</small>
                  <h4 className="mb-0 mt-1 text-danger">{baht(overall.outstanding)}</h4>
                  <small className="text-muted">ค้าง {overall.unpaid_count} · บางส่วน {overall.partial_count}</small>
                </Card.Body></Card>
              </Col>
              <Col md={6} xl={3}>
                <Card className="h-100 border-info"><Card.Body>
                  <small className="text-muted">อัตราการเก็บได้</small>
                  <h4 className="mb-0 mt-1">
                    {overall.total_amount > 0 ? Math.round((overall.paid_amount / overall.total_amount) * 100) : 0}%
                  </h4>
                </Card.Body></Card>
              </Col>
            </Row>
          )}

          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-2 mb-3">
                <Col md="auto">
                  <Form.Select value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)} style={{ minWidth: 180 }} disabled={!selectedTerm}>
                    <option value="">ทุกสถานะ</option>
                    <option value="unpaid">ค้างชำระ</option>
                    <option value="partial">ชำระบางส่วน</option>
                    <option value="paid">ชำระครบ</option>
                    <option value="cancelled">ยกเลิก</option>
                  </Form.Select>
                </Col>
                <Col md={5}>
                  <InputGroup>
                    <Form.Control
                      placeholder="ค้นหา รหัส/ชื่อ/เลขบิล แล้วกด Enter"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchInvoices(selectedTerm)}
                      disabled={!selectedTerm}
                    />
                    <Button variant="primary" onClick={() => fetchInvoices(selectedTerm)} disabled={!selectedTerm}>
                      <i className="ri-search-line me-1" />ค้นหา
                    </Button>
                  </InputGroup>
                </Col>
              </Row>

              {capped && (
                <Alert variant="warning" className="py-2">
                  แสดงเฉพาะ {PAGE_LIMIT} รายการแรก — โปรดพิมพ์คำค้นหาหรือเลือกสถานะเพื่อให้แคบลง
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : !searched ? (
                <div className="text-center text-muted py-5">
                  <i className="ri-search-2-line fs-1 d-block mb-2" />
                  พิมพ์คำค้นหา (รหัส/ชื่อ/เลขบิล) หรือเลือกสถานะ เพื่อแสดงรายการบิล
                  <div className="small mt-1">ดูยอดรวมทั้งงวดได้จากการ์ดสรุปด้านบน</div>
                </div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>เลขบิล</th>
                      <th>รหัส</th>
                      <th>ชื่อ-สกุล</th>
                      <th>ชั้น</th>
                      <th className="text-end">รวม</th>
                      <th className="text-end">ชำระแล้ว</th>
                      <th className="text-end">คงค้าง</th>
                      <th className="text-center">สถานะ</th>
                      <th className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length === 0 ? (
                      <tr><td colSpan={9} className="text-center text-muted py-4">ไม่พบบิลตามเงื่อนไข</td></tr>
                    ) : (
                      invoices.map((inv) => (
                        <tr key={inv.invoice_id}>
                          <td>{inv.invoice_no}</td>
                          <td>{inv.st_idnumber}</td>
                          <td>{inv.st_name}</td>
                          <td>{inv.c_class || '-'}</td>
                          <td className="text-end">{baht(inv.total_amount)}</td>
                          <td className="text-end">{baht(inv.paid_amount)}</td>
                          <td className="text-end fw-medium">{baht(inv.balance)}</td>
                          <td className="text-center">
                            <Badge bg={STATUS[inv.invoice_status]?.bg || 'secondary'}>
                              {STATUS[inv.invoice_status]?.label || inv.invoice_status}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Button size="sm" variant="outline-primary" onClick={() => navigate(`/fee/invoices/${inv.invoice_id}`)}>
                              <i className="ri-eye-line me-1" />ดู/ชำระ
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </main>

      <Modal show={showGenModal} onHide={() => !generating && setShowGenModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ออกบิลทั้งงวด</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {genResult ? (
            <Alert variant="success" className="mb-0">
              <div>ออกบิลใหม่: <strong>{genResult.created}</strong> ใบ (ยอดรวม {baht(genResult.total_amount)} บาท)</div>
              <div className="text-muted mt-1">
                ข้ามที่มีบิลแล้ว: {genResult.skipped_existing} · ไม่มีรายการเก็บ: {genResult.skipped_no_items}
              </div>
            </Alert>
          ) : (
            <>
              <p className="mb-2">
                ระบบจะออกใบแจ้งหนี้ให้นักเรียนที่ลงทะเบียนในงวด <strong>{currentTermLabel}</strong>
                โดยคิดราคาตามตารางราคาที่ตั้งไว้ (สามัญ + ศาสนา + เก็บทุกคน)
              </p>
              <p className="text-muted mb-0">
                นักเรียนที่มีบิลในงวดนี้แล้วจะถูกข้าม (ไม่กระทบยอดที่ชำระไปแล้ว)
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {genResult ? (
            <Button variant="primary" onClick={() => setShowGenModal(false)}>เสร็จสิ้น</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowGenModal(false)} disabled={generating}>ยกเลิก</Button>
              <Button variant="primary" onClick={handleGenerate} disabled={generating}>
                {generating ? 'กำลังออกบิล...' : 'ยืนยันออกบิล'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default FeeInvoices;
